import React, { useEffect, useRef, useState } from "react";
import { Search, X, Sparkles, TrendingUp, Coins } from "lucide-react";
import Header from "../components/Header";
import SearchResults from "../components/SearchResults";
import BulkSearch from "../components/BulkSearch";
import FavouritesSheet from "../components/FavouritesSheet";
import FaqSection from "../components/FaqSection";
import AdSlot from "../components/AdSlot";
import { EXAMPLE_QUERIES, STATUS_META } from "../mock";
import { CURRENCY_CODES } from "../data/currency";
import useDomainSearch from "../hooks/useDomainSearch";
import useFavourites from "../hooks/useFavourites";

function Hero() {
  return (
    <div className="mb-10 text-center">
      <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
        Find the perfect domain.
      </h1>
      <p className="mt-4 text-lg text-zinc-500">
        Instantly check availability across hundreds of TLDs — and discover
        clever domain hacks as you type.
      </p>
    </div>
  );
}

function SearchBar({ query, onChange, onClear, inputRef }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" />
      <input
        ref={inputRef}
        value={query}
        onChange={onChange}
        placeholder="Search for a domain…"
        autoFocus
        spellCheck={false}
        data-testid="domain-search-input"
        className="w-full rounded-xl border border-white/10 bg-[#141417] py-4 pl-12 pr-12 text-lg text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20"
      />
      {query.trim().length > 0 && (
        <button
          onClick={onClear}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 transition-colors hover:text-white"
          aria-label="Clear"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function ModeCurrencyBar({ bulkMode, setBulkMode, currency, setCurrency }) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
        {[
          { key: false, label: "Single" },
          { key: true, label: "Bulk" },
        ].map((m) => (
          <button
            key={m.label}
            onClick={() => setBulkMode(m.key)}
            data-testid={`mode-${m.label.toLowerCase()}-btn`}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              bulkMode === m.key ? "bg-white text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="inline-flex items-center gap-1.5" data-testid="currency-switcher">
        <Coins className="h-3.5 w-3.5 text-zinc-600" />
        {CURRENCY_CODES.map((c) => (
          <button
            key={c}
            onClick={() => setCurrency(c)}
            data-testid={`currency-${c}-btn`}
            className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              currency === c ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultsControls({ availableOnly, setAvailableOnly, sortBy, setSortBy }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <button
        onClick={() => setAvailableOnly((v) => !v)}
        data-testid="available-only-toggle"
        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
          availableOnly
            ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400"
            : "border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white"
        }`}
      >
        Available only
      </button>
      <div className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
        <span>Sort</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="sort-select"
          className="rounded-md border border-white/10 bg-[#141417] px-2 py-1 text-xs text-white outline-none focus:border-emerald-400/60"
        >
          <option value="relevance">Relevance</option>
          <option value="price">Price (low→high)</option>
          <option value="az">A–Z</option>
        </select>
      </div>
    </div>
  );
}

function TrendingChips({ trending, chips, onPick }) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
      <span className="flex items-center gap-1 text-sm text-zinc-600">
        {trending.length > 0 ? (
          <>
            <TrendingUp className="h-3.5 w-3.5" /> Trending:
          </>
        ) : (
          <>
            <Sparkles className="h-3.5 w-3.5" /> Try:
          </>
        )}
      </span>
      {chips.map((q) => (
        <button
          key={q}
          onClick={() => onPick(q)}
          className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-sm text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/[0.06]"
        >
          {q}
        </button>
      ))}
    </div>
  );
}

function StatusLegend() {
  return (
    <section className="mt-16 pb-20">
      <p className="mb-4 text-center text-xs uppercase tracking-widest text-zinc-600">
        Status guide
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {Object.entries(STATUS_META).map(([key, meta]) => (
          <div key={key} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: meta.color }}
            />
            <span className="text-sm text-zinc-400">{meta.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-white/10">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-6 text-sm text-zinc-600 sm:flex-row">
        <span>ezidomains.info — fast domain search</span>
        <div className="flex items-center gap-5">
          <a href={process.env.REACT_APP_TERMS_URL || "#"} target="_blank" rel="noreferrer" className="transition-colors hover:text-zinc-300">Terms</a>
          <a href={process.env.REACT_APP_PRIVACY_URL || "#"} target="_blank" rel="noreferrer" className="transition-colors hover:text-zinc-300">Privacy</a>
          <a href={process.env.REACT_APP_REGISTER_URL || "#"} target="_blank" rel="noreferrer" className="transition-colors hover:text-zinc-300">Domains</a>
        </div>
      </div>
    </footer>
  );
}

const CURRENCY_KEY = "ezi_currency";

export default function Home() {
  const search = useDomainSearch();
  const favs = useFavourites();
  const inputRef = useRef(null);

  const [bulkMode, setBulkMode] = useState(false);
  const [favouritesOpen, setFavouritesOpen] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [currency, setCurrency] = useState(
    () => localStorage.getItem(CURRENCY_KEY) || "USD"
  );

  useEffect(() => {
    localStorage.setItem(CURRENCY_KEY, currency);
  }, [currency]);

  const hasQuery = search.query.trim().length > 0;
  const chips =
    search.trending.length > 0
      ? search.trending.map((t) => t.query)
      : EXAMPLE_QUERIES;

  const clearQuery = () => {
    search.setQuery("");
    inputRef.current?.focus();
  };

  const openSingle = (name) => {
    setBulkMode(false);
    search.setQuery(name);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Header
        onOpenFavourites={() => setFavouritesOpen(true)}
        favouritesCount={favs.favourites.length}
      />

      <main className="mx-auto max-w-3xl px-5">
        <section
          className={`transition-all duration-500 ${
            hasQuery || bulkMode ? "pt-10" : "pt-24 sm:pt-32"
          }`}
        >
          {!hasQuery && !bulkMode && <Hero />}

          <ModeCurrencyBar
            bulkMode={bulkMode}
            setBulkMode={setBulkMode}
            currency={currency}
            setCurrency={setCurrency}
          />

          {bulkMode ? (
            <BulkSearch onOpenSingle={openSingle} />
          ) : (
            <>
              <div className="mt-4">
                <SearchBar
                  query={search.query}
                  onChange={(e) => search.setQuery(e.target.value)}
                  onClear={clearQuery}
                  inputRef={inputRef}
                />
              </div>
              {!hasQuery && (
                <TrendingChips trending={search.trending} chips={chips} onPick={search.setQuery} />
              )}
            </>
          )}
        </section>

        {!bulkMode && hasQuery && (
          <section className="mt-6 pb-20">
            <ResultsControls
              availableOnly={availableOnly}
              setAvailableOnly={setAvailableOnly}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
            <SearchResults
              results={search.results}
              loading={search.loading}
              query={search.debounced}
              currency={currency}
              sortBy={sortBy}
              availableOnly={availableOnly}
              favourites={favs.favourites}
              onToggleFavourite={favs.toggle}
            />
            <AdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_RESULTS} className="mt-8" />
          </section>
        )}

        {!bulkMode && !hasQuery && (
          <>
            <StatusLegend />
            <FaqSection />
          </>
        )}
      </main>

      <SiteFooter />

      <FavouritesSheet
        open={favouritesOpen}
        onOpenChange={setFavouritesOpen}
        favourites={favs.favourites}
        onRemove={favs.remove}
        onClear={favs.clear}
        onOpenSingle={openSingle}
      />
    </div>
  );
}
